import React, { useState, useEffect } from "react";
import "./AnnouncementsDisplay.scss";
import axios from "axios";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const AnnouncementsDisplay = ({ limit }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch announcements from your API
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
        const response = await axios.get(
          `${BE_URL}/api/general/announcements`,
          {
            headers: { Authorization: `Bearer ${tokens.accessToken}` },
          }
        );

        // Apply limit if provided
        const limitedAnnouncements = limit
          ? response.data.slice(0, limit)
          : response.data;

        setAnnouncements(limitedAnnouncements);
        setError(null);
      } catch (error) {
        console.error("Failed to fetch announcements:", error);
        setError("Failed to load announcements. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [limit]);

  const formatDateAndTime = (timestamp) => {
    if (!timestamp) return "N/A";

    const date = new Date(timestamp);

    // Format date in a more readable way
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    // Format time in 12-hour format
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return { date: formattedDate, time: formattedTime };
  };

  // Get time elapsed since announcement
  const getTimeElapsed = (timestamp) => {
    if (!timestamp) return "N/A";

    const now = new Date();
    const announcementDate = new Date(timestamp);
    const diffInMs = now - announcementDate;

    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 60) {
      return diffInMins === 1 ? "1 minute ago" : `${diffInMins} minutes ago`;
    } else if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    } else if (diffInDays < 7) {
      return diffInDays === 1 ? "Yesterday" : `${diffInDays} days ago`;
    } else {
      return formatDateAndTime(timestamp).date;
    }
  };

  // Get appropriate icon based on announcement category/priority
  const getAnnouncementIcon = (announcement) => {
    const priority = announcement.priority?.toLowerCase() || "";
    const category = announcement.category?.toLowerCase() || "";

    if (priority === "high" || priority === "urgent") {
      return "🔴";
    } else if (category === "event") {
      return "📅";
    } else if (category === "notice") {
      return "📢";
    } else if (category === "academic") {
      return "📚";
    } else if (category === "holiday") {
      return "🏖️";
    } else {
      return "📌";
    }
  };

  if (loading) {
    return (
      <div className="announcements-loading">
        <div className="loading-spinner"></div>
        <p>Loading announcements...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="announcements-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="announcements-display">
      {announcements.length > 0 ? (
        announcements.map((announcement) => {
          const { date, time } = formatDateAndTime(announcement.created_at);
          const timeElapsed = getTimeElapsed(announcement.created_at);

          return (
            <div
              key={announcement.id}
              className={`announcement-card ${
                announcement.priority?.toLowerCase() === "high"
                  ? "high-priority"
                  : ""
              }`}
            >
              <div className="announcement-icon">
                {getAnnouncementIcon(announcement)}
              </div>

              <div className="announcement-content-wrapper">
                <div className="announcement-header">
                  <h3 className="announcement-title">{announcement.subject}</h3>
                  <div className="announcement-meta">
                    <span className="announcement-time-elapsed">
                      {timeElapsed}
                    </span>
                    <span className="announcement-category">
                      {announcement.category || "General"}
                    </span>
                  </div>
                </div>

                <div className="announcement-body">
                  <p className="announcement-content">{announcement.body}</p>
                </div>

                <div className="announcement-footer">
                  <div className="announcement-date-time">
                    <span className="announcement-date">{date}</span>
                    <span className="announcement-time">{time}</span>
                  </div>

                  {announcement.author && (
                    <div className="announcement-author">
                      Posted by: {announcement.author}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="no-announcements">
          <div className="no-data-icon">📮</div>
          <p>No announcements available at this time.</p>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsDisplay;
